import { useState, useEffect, useCallback } from 'react';
import { User, LogOut, Loader2, X } from 'lucide-react';
import { authService, hasTeacherOrAboveAccess, type User as AuthUser, TL4KHeaderButton } from '@tl4k/frontend-shared';
import AdminLogin from './AdminLogin';

export interface AdminSessionToolbarProps {
  /** Dopo logout (opzionale). */
  onLoginSuccess?: () => void;
  /** URL di ritorno opzionale per il login OAuth. */
  loginReturnTo?: string;
  /**
   * Se true, nasconde il pulsante Admin per utenti non autenticati via OAuth
   * (es. studente in sessione PIN/pseudonimo). Docenti autenticati restano visibili.
   */
  hideAdminLogin?: boolean;
}

/**
 * Controlli sessione admin in header: pulsante **Admin** apre la modale con `AdminLogin`
 * (Google + profili dev). Visibile solo a utenti non autenticati (salvo hideAdminLogin) o docenti/admin.
 */
function AdminSessionToolbar({ onLoginSuccess, loginReturnTo, hideAdminLogin = false }: AdminSessionToolbarProps) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [showAdminModal, setShowAdminModal] = useState(false);

  const checkAuth = useCallback(async () => {
    try {
      const currentUser = await authService.getCurrentUser();
      setUser(currentUser);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void checkAuth();
    const handleAuthUpdated = () => void checkAuth();
    window.addEventListener('tl4k-auth-updated', handleAuthUpdated);
    return () => window.removeEventListener('tl4k-auth-updated', handleAuthUpdated);
  }, [checkAuth]);

  useEffect(() => {
    if (user) setShowAdminModal(false);
  }, [user]);

  useEffect(() => {
    if (!showAdminModal) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowAdminModal(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showAdminModal]);

  const handleLogout = async () => {
    await authService.logout();
    setUser(null);
    onLoginSuccess?.();
  };

  const handleAdminLoginSuccess = () => {
    void checkAuth();
    setShowAdminModal(false);
    onLoginSuccess?.();
  };

  // Studente in sessione (PIN): nessun utente OAuth → niente pulsante Admin
  if (hideAdminLogin && !user) {
    return null;
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-100 rounded-lg">
        <Loader2 className="w-4 h-4 animate-spin text-slate-500" />
      </div>
    );
  }

  if (user) {
    if (hasTeacherOrAboveAccess(user)) {
      return (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexWrap: 'wrap',
            justifyContent: 'flex-end',
            minWidth: 0,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '4px 10px',
              background: '#eff6ff',
              borderRadius: 8,
              maxWidth: 200,
              minWidth: 0,
            }}
          >
            {user.photoURL ? (
              <img
                src={user.photoURL}
                alt={user.displayName || 'Utente'}
                style={{
                  width: 24,
                  height: 24,
                  minWidth: 24,
                  minHeight: 24,
                  borderRadius: '9999px',
                  flexShrink: 0,
                  objectFit: 'cover',
                }}
              />
            ) : (
              <User className="w-4 h-4 text-blue-600 shrink-0" />
            )}
            <span
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: '#334155',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                minWidth: 0,
              }}
            >
              {user.displayName || user.email}
            </span>
          </div>
          <button
            type="button"
            onClick={handleLogout}
            style={{
              padding: 6,
              borderRadius: 8,
              color: '#64748b',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
            }}
            title="Esci"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      );
    }

    return (
      <button
        type="button"
        onClick={handleLogout}
        className="flex items-center gap-2 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
        title="Esci"
      >
        <LogOut className="w-4 h-4" />
        Esci
      </button>
    );
  }

  return (
    <>
      <TL4KHeaderButton
        variant="accent"
        icon={User}
        onClick={() => setShowAdminModal(true)}
        title="Accesso amministrazione TL4K"
      >
        Admin
      </TL4KHeaderButton>

      {showAdminModal && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Accesso amministrazione"
        >
          <div className="relative max-h-[min(90vh,720px)] w-full max-w-lg overflow-y-auto rounded-xl bg-white shadow-2xl">
            <button
              type="button"
              className="absolute right-2 top-2 z-10 rounded-lg p-2 text-slate-500 hover:bg-slate-100"
              onClick={() => setShowAdminModal(false)}
              aria-label="Chiudi"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="[&>div]:min-h-0 [&>div]:rounded-b-xl">
              <AdminLogin
                title="Accesso amministrazione"
                description="Accedi con Google o, in sviluppo, usa i profili di test qui sotto."
                onLoginSuccess={handleAdminLoginSuccess}
                loginReturnTo={loginReturnTo}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default AdminSessionToolbar;
